import { z } from 'zod'

// The rollup's five signal counters live beside the per-app `SignalFreshness`
// they count, not here: one vocabulary, one place. Imported rather than
// restated so a counter cannot be added on one side and missed on the other.
import { SignalCoverageCountsSchema } from './mobile-app-readiness'

/**
 * A **publishing account** — an Apple Developer team or a Google Play developer
 * account the fleet ships under (publishing-accounts plan, KD1/KD2).
 *
 * The vocabularies below are the closed unions the database deliberately does
 * NOT enforce. Ownership, kind and API-access state are string columns rather
 * than database enums (KTD5) because each new enum value costs a hand-performed
 * schema promotion and all three are expected to grow. THIS FILE is where the
 * closed set actually lives, so a value is validated here rather than by MySQL.
 */

/** Apple multiplies per school; Play consolidates onto one account. One model spans both (KD3). */
export const PublishingAccountVendorSchema = z.enum(['apple', 'google-play'])
export type PublishingAccountVendor = z.infer<typeof PublishingAccountVendorSchema>

/**
 * Who holds the account (R3, KD9).
 *
 * `unanswered` is a REAL STATE and the default, never a placeholder to be tidied
 * away. Recording a school-owned team as `ours` reports a credential gap we can
 * never close; the operator surface would ask someone to fix it forever.
 */
export const PublishingAccountOwnershipSchema = z.enum(['ours', 'third_party', 'unanswered'])
export type PublishingAccountOwnership = z.infer<typeof PublishingAccountOwnershipSchema>

/**
 * What the account can DO — independent of who owns it (KD10).
 *
 * A personal Apple account cannot issue team credentials no matter who holds it,
 * so capability and ownership are separate axes and collapsing them misreports
 * both. `unanswered` exists for the same reason it does on ownership (KTD14):
 * the seed derives records from app rows, where kind is not knowable.
 */
export const PublishingAccountKindSchema = z.enum(['organization', 'personal', 'unanswered'])
export type PublishingAccountKind = z.infer<typeof PublishingAccountKindSchema>

/**
 * Can a vaulted credential read this account right now (R1, KTD1)?
 *
 * ONE AXIS FOR EVERY VENDOR, and it is deliberately NOT called a capability:
 * `AppReadinessCapability` and `CapabilityKey` already own that word elsewhere
 * in this package, and two meanings of "capability" on one screen is how a
 * reader stops trusting either.
 *
 * THREE VALUES, and the third is the point. `never_checked` covers both nobody
 * has looked and a look that never completed — either way nothing has been
 * observed, and an unobserved account is never reported as healthy (AE3).
 *
 * `clear` rather than `accepted` keeps the honesty the Apple-only vocabulary
 * this replaces was careful about: a passing read proves a read succeeded, not
 * that the vendor gates that path on anything in particular.
 */
export const PublishingAccountApiAccessStateSchema = z.enum(['clear', 'blocked', 'never_checked'])
export type PublishingAccountApiAccessState = z.infer<typeof PublishingAccountApiAccessStateSchema>

/**
 * WHICH obstacle is blocking the read (R2).
 *
 * A CLOSED SET, non-null exactly when the state is `blocked`. The obstacle is
 * what differs between vendors — Apple refuses an unaccepted developer agreement,
 * Play refuses an unlinked project or a service account without the permission,
 * and either refuses a key it will not accept — so the vendor difference lives
 * HERE, in a reason, rather than in a second per-vendor state axis.
 */
export const PublishingAccountApiAccessReasonSchema = z.enum([
  /** Apple will not serve the read until someone accepts an agreement in the portal. */
  'apple_agreement_required',
  /** The vendor rejected the key itself. Filed, and unusable as filed. */
  'key_rejected',
  /** The Play developer account is not linked to the Google Cloud project the key belongs to. */
  'play_project_not_linked',
  /** The service account authenticates and lacks the permission the read needs. */
  'play_insufficient_permission'
])
export type PublishingAccountApiAccessReason = z.infer<typeof PublishingAccountApiAccessReasonSchema>

/**
 * What unblocks a `blocked` account, AS DATA (R2).
 *
 * The step and its link travel in the readiness payload rather than living in
 * skill prose or in a screen's copy, because three surfaces need the same
 * sentence — the operator screen, the readiness API and whatever agent is asked
 * "what is stopping this account" — and prose kept in one of them is wrong in
 * the other two the first time a vendor moves a page.
 */
export const PublishingAccountApiAccessBlockedStepSchema = z.object({
  reason: PublishingAccountApiAccessReasonSchema,
  /** One imperative sentence naming what a HUMAN must do. Never a description of the state. */
  humanStep: z.string(),
  /** Where they do it — the vendor console page, not a documentation page. */
  url: z.string()
})
export type PublishingAccountApiAccessBlockedStep = z.infer<typeof PublishingAccountApiAccessBlockedStepSchema>

/**
 * LEGACY — the Apple-shaped agreement vocabulary the column used to speak, kept
 * for exactly one purpose: the wire field `agreementState`, which deployed
 * clients still read.
 *
 * Nothing in the platform DERIVES anything from this any more. Stored values are
 * mapped into {@link PublishingAccountApiAccessStateSchema} once, inside the
 * backend's account store, and the wire value is projected back out of the new
 * vocabulary by {@link legacyAgreementStateFrom}. Both directions exist so the
 * rename is reversible: reverting the new code without first rewriting the
 * stored values would leave `blocked` rows reading as no reason at all.
 */
export const PublishingAccountAgreementStateSchema = z.enum([
  'never_checked',
  'not_observed_blocked',
  'requires_action',
  'check_failed'
])
export type PublishingAccountAgreementState = z.infer<typeof PublishingAccountAgreementStateSchema>

/**
 * Project the API-access axis back into the legacy agreement vocabulary (KTD8).
 *
 * ONE MAPPING, EXPORTED ONCE, because it is applied in two places that must not
 * disagree: the serializer that still emits `agreementState` for deployed
 * clients, and the revert script that rewrites stored values back if this change
 * is rolled back.
 *
 * `stale` carries the value the new vocabulary dropped. Staleness is a property
 * of `apiAccessObservedAt` rather than a state, but the legacy vocabulary had a
 * value for "the last check tells us nothing current" (`check_failed`), so a
 * reading nobody has refreshed lately is reported to legacy readers exactly the
 * way they already treat that value: unknown, not healthy.
 *
 * `blocked` OUTRANKS `stale`, and it never maps to `not_observed_blocked`
 * whatever its reason. Both halves of that are the collapse this mapping exists
 * to prevent: the legacy reader treats `not_observed_blocked` as the clean state
 * and `check_failed` as merely unknown, so a blocked account arriving at either
 * would stop reporting the one thing about it that needs a human.
 */
export function legacyAgreementStateFrom(
  state: PublishingAccountApiAccessState,
  // Carried so callers hand over the whole triple they hold, and so a reason
  // that later needs its own legacy value has a seam to land in. The legacy
  // vocabulary has no room for a reason today, so it does not change the answer.
  reason: PublishingAccountApiAccessReason | null,
  stale: boolean
): PublishingAccountAgreementState {
  if (state === 'blocked') return 'requires_action'
  if (stale) return 'check_failed'
  if (state === 'clear') return 'not_observed_blocked'
  return 'never_checked'
}

/**
 * How far the account has got with the vendor (credential-home plan, R20/R21/R23).
 *
 * A PROVISIONING AXIS, independent of ownership, kind and the API-access state
 * above — an account can be third-party, personal, agreement-unchecked and
 * `api_ready` all at once, and collapsing any pair of those misreports both.
 *
 *   `awaiting_account` — the vendor has not issued or approved this account yet.
 *                        Such a row carries a placeholder identifier
 *                        (`pending-<schoolPublicId>`) that the real one replaces.
 *   `console_only`     — the account exists and is reachable only by a human
 *                        signing into the vendor's console. Nothing vaulted.
 *   `api_ready`        — a vaulted credential can call the vendor's API for it.
 *
 * OBSERVATION, NEVER CONCLUSION (R23) — the rule `apiAccessState` states one
 * field up, applied here. Nothing may set `api_ready` because a credential row
 * exists; it is set once a call actually succeeded. An account nobody has
 * checked is not ready, and saying otherwise is how a provisioning run finds out
 * mid-flight that it cannot proceed.
 *
 * `awaiting_account` IS THE DEFAULT — the least-claiming of the three, and the
 * unanswered-equivalent on this axis. Every pre-existing row lands there until
 * something observes otherwise. Under-reporting makes an operator repeat a
 * provisioning step; over-reporting makes them skip one.
 */
export const PublishingAccountProvisioningStateSchema = z.enum(['awaiting_account', 'console_only', 'api_ready'])
export type PublishingAccountProvisioningState = z.infer<typeof PublishingAccountProvisioningStateSchema>

/**
 * The reserved prefix an account identifier carries before the vendor has
 * issued a real one (R20, KTD5).
 *
 * WHY A PREFIX AND NOT A BARE CUID. `PublishingAccount` is
 * `@@unique([vendor, accountId])` with both components non-nullable, so a
 * pending account still needs a real, per-account-unique identifier — an
 * empty-string sentinel would collide the moment two schools each await an
 * account of the same vendor. A bare cuid solves the collision but not the
 * READING: the backfill copies this value onto `MobileApp.appleTeamId` /
 * `.playAccountId`, where every vendor-facing path treats it as a real vendor
 * id, and a cuid there is indistinguishable from one. The prefix makes it
 * decidable by any reader in isolation.
 *
 * IT LIVES HERE, in shared-types, rather than in the backend's account store,
 * because BOTH runtimes have to decide it: the backend guards two chokepoints
 * with it and the admin screen labels a pending account with it. Two spellings
 * of one sentinel is how a placeholder ends up recognised on one side and
 * treated as a real vendor id on the other.
 */
export const PLACEHOLDER_ACCOUNT_ID_PREFIX = 'pending-'

/**
 * Compose the placeholder identifier for a school's not-yet-issued account.
 *
 * The SCHOOL's public id, not the app's: an account belongs to a school and one
 * school holds exactly one pending account per vendor, so this is the narrowest
 * value that keeps the natural key firing. At `pending-` plus a cuid it is about
 * 33 characters and fits `accountId`'s `VarChar(64)`.
 */
export function buildPlaceholderAccountId(schoolPublicId: string): string {
  return `${PLACEHOLDER_ACCOUNT_ID_PREFIX}${schoolPublicId}`
}

/**
 * Is this identifier a placeholder rather than something the vendor issued?
 *
 * THE SINGLE PREDICATE (KTD5). On the backend it guards two chokepoints — a
 * placeholder is never passed to a vendor call, and a credential is never filed
 * against one. Both are guards rather than live problems today: nothing can
 * vault a credential for an account the vendor has not created. They are the two
 * ways a placeholder would otherwise leave the record and reach an API.
 *
 * It cannot produce a false positive on a real identifier: an Apple team id is
 * 10 alphanumeric characters and a Play developer id is 19 digits, and neither
 * can begin with `pending-`.
 */
export function isPlaceholderAccountId(accountId: string): boolean {
  return accountId.startsWith(PLACEHOLDER_ACCOUNT_ID_PREFIX)
}

/**
 * The single answer automation acts on (KTD13). `unknown` is a first-class
 * outcome, not a shrug: reporting an unprobed account as `can_ship` is how a run
 * discovers mid-flight that it cannot.
 */
export const ReadinessHeadlineSchema = z.enum(['can_ship', 'unknown', 'cannot_ship'])
export type ReadinessHeadline = z.infer<typeof ReadinessHeadlineSchema>

/**
 * Every reason a readiness walk can contribute.
 *
 * NOT MUTUALLY EXCLUSIVE, and the surface must never file a row into a single
 * bucket: a refused personal account carries BOTH `refused` and
 * `manual_steps_required`, and neither may mask the other (AE13).
 */
export const ReadinessReasonSchema = z.enum([
  /** The account is refused. The incident lever (KD8, R9). */
  'refused',
  /** Nobody has said whether this is an organization or a personal account (KTD14). */
  'kind_unanswered',
  /** A structural limit, not a defect (R18) — a personal account cannot hold team credentials. */
  'manual_steps_required',
  /** Nothing filed, and the account-scoped import has not run yet: not a gap anyone can act on (KTD4). */
  'credentials_not_yet_imported',
  /** Nothing filed, and the import has run. A real gap. */
  'no_credentials',
  /** Key material is on file and cannot be read. Not an empty slot. */
  'credentials_unreadable',
  /** Nobody has observed whether a credential can read this account (AE3). */
  'api_access_never_checked',
  /** The last observation is too old to act on — including a probe that never completed (AE11). */
  'api_access_stale',
  /** The vendor refused the read. `apiAccessReason` names which obstacle (R2). */
  'api_access_blocked'
])
export type ReadinessReason = z.infer<typeof ReadinessReasonSchema>

/**
 * LEGACY — the Apple-shaped reason strings the readiness walk emitted before the
 * API-access rename, kept for exactly one release (KTD8, R17).
 *
 * DEPRECATED. Removed with the rest of the bridge in U11/PR4, together with
 * {@link withLegacyReadinessReasons} and the reader half in the dashboard's
 * `publishing-account-readiness-wire` module.
 *
 * WHY A BRIDGE AT ALL, when the three renamed strings live in one array rather
 * than a field of their own. A reason is not a cosmetic label on this screen: it
 * drives the Next-step column and the action lane. A dashboard bundle built
 * before the rename keys every one of those on `agreement_*`, so the release
 * that shipped only the new spellings would give it an empty badge, an empty
 * tooltip, a Next-step column reading "—" and a lane of `ours-fixable` — blank
 * cells and a wrong lane in exactly the rows that need a human, which is the
 * failure R17 and AE5 exist to forbid.
 */
export const LegacyReadinessReasonSchema = z.enum([
  'agreement_never_checked',
  'agreement_check_did_not_complete',
  'agreement_requires_action'
])
export type LegacyReadinessReason = z.infer<typeof LegacyReadinessReasonSchema>

/**
 * The renamed pairs, ONE MAPPING EXPORTED ONCE, in the shape and the place
 * {@link legacyAgreementStateFrom} already established for the state axis.
 *
 * Only these three were renamed. Every other reason keeps its spelling and has
 * no twin, which is why the write direction returns `null` rather than widening
 * a reason that never moved.
 */
const LEGACY_READINESS_REASONS: Readonly<Partial<Record<ReadinessReason, LegacyReadinessReason>>> = {
  api_access_never_checked: 'agreement_never_checked',
  api_access_stale: 'agreement_check_did_not_complete',
  api_access_blocked: 'agreement_requires_action'
}

/** The legacy twin of a renamed reason, or `null` for a reason the rename left alone. */
export function legacyReadinessReasonFrom(reason: ReadinessReason): LegacyReadinessReason | null {
  return LEGACY_READINESS_REASONS[reason] ?? null
}

/**
 * The READ direction, total over the legacy vocabulary.
 *
 * Both directions exist for the reason `legacyAgreementStateFrom`'s docblock
 * gives: the rename has to be reversible, and a reader that meets a bridged
 * payload must land on the canonical reason rather than on a second spelling it
 * then has to keep total maps for.
 */
export function readinessReasonFromLegacy(legacy: LegacyReadinessReason): ReadinessReason {
  switch (legacy) {
    case 'agreement_never_checked':
      return 'api_access_never_checked'
    case 'agreement_check_did_not_complete':
      return 'api_access_stale'
    case 'agreement_requires_action':
      return 'api_access_blocked'
  }
}

/**
 * A reason AS IT TRAVELS during the bridge release: either vocabulary.
 *
 * The wire is widened rather than swapped because the bridge must be a pure
 * DELETION at U11/PR4. Emitting only the legacy spellings would render correctly
 * in both bundles today and then make the removal a second breaking change.
 */
export const ReadinessReasonWireSchema = z.union([ReadinessReasonSchema, LegacyReadinessReasonSchema])
export type ReadinessReasonWire = z.infer<typeof ReadinessReasonWireSchema>

/**
 * Project a verdict's reasons onto the wire, each renamed reason followed
 * immediately by its legacy twin.
 *
 * INTERLEAVED, not appended: a twin sitting beside the reason it translates is
 * readable in a payload dump and keeps the pre-rename bundle's badge order
 * matching the new one's. A bundle built before the rename skips the spelling it
 * does not know and renders the twin; a bundle built after it collapses the pair
 * back to one canonical reason.
 */
export function withLegacyReadinessReasons(reasons: readonly ReadinessReason[]): ReadinessReasonWire[] {
  return reasons.flatMap(reason => {
    const legacy = legacyReadinessReasonFrom(reason)
    return legacy === null ? [reason] : [reason, legacy]
  })
}

/**
 * A headline plus every reason that applies (KTD13).
 *
 * The headline is the worst outcome any reason implies, so automation reads one
 * field and every consumer agrees. The screen renders the headline as the row's
 * status and each reason as its own badge.
 *
 * `reasons` is typed on the WIRE union for the bridge release, so the array a
 * client actually receives is the array the contract describes. Producers keep
 * building the canonical union and hand it to {@link withLegacyReadinessReasons}
 * at the serializer; the widening reverts with the bridge.
 */
export const ReadinessVerdictSchema = z.object({
  headline: ReadinessHeadlineSchema,
  reasons: z.array(ReadinessReasonWireSchema)
})
export type ReadinessVerdict = z.infer<typeof ReadinessVerdictSchema>

/**
 * What one API-access probe CONCLUDED, as distinct from what the account now
 * holds (R3).
 *
 * IT LIVES HERE because both runtimes decide on it. The backend writes a column
 * only for the first two; the dashboard has to tell a probe that determined
 * something from one that did not, and the account row alone cannot tell it —
 * a `check_failed` attempt writes NO column, so the readiness that comes back
 * is byte-identical to the one that went in. Without this value on the response
 * the only signal left is prose in `message`, and a screen that string-matches
 * prose to decide whether to show a green toast is a screen that shows a green
 * toast the day the prose is reworded.
 */
export const ApiAccessProbeOutcomeSchema = z.enum(['clear', 'blocked', 'check_failed'])
export type ApiAccessProbeOutcome = z.infer<typeof ApiAccessProbeOutcomeSchema>

/**
 * HOW ONE observed standing value was obtained (Track I, D5).
 *
 * Ships in v1 even though the interpreter tier that produces `interpreted` is
 * deferred: every deterministic portal read writes `scraped`, so the split is
 * recorded from the first run. Deferring the vocabulary with the interpreter
 * would leave every value written before the split untaggable — the
 * scraped-versus-interpreted discipline the read lane lives or dies by could
 * never be reconstructed for them.
 *
 * Stored as JSON on `PublishingAccountObservedStanding.fieldProvenance`, not as
 * a database enum (KTD5): `interpreted` is a KNOWN future value, and a DB enum
 * would put a hand-performed PlanetScale promotion in front of it, which is
 * exactly what shipping the vocabulary early exists to avoid.
 */
export const ObservedStandingProvenanceSchema = z.enum(['scraped', 'interpreted'])
export type ObservedStandingProvenance = z.infer<typeof ObservedStandingProvenanceSchema>

/**
 * Provenance PER FIELD, which is the only granularity that can describe a real
 * reading.
 *
 * One provenance for the whole row cannot express the case the interpreter tier
 * exists to handle: when a portal moves part of its page, the deterministic
 * scraper keeps reading the fields whose selectors still match and the
 * interpreter recovers only the ones that broke. The reading then holds both
 * kinds at once, and a single row-level label is wrong about most of its own
 * values — which destroys exactly the separability D5 ships this vocabulary
 * early to preserve.
 *
 * Keyed by the observed field's camelCase name (`renewalDate`, `entityName`,
 * `ourRole`, `enrolledAs`, `accountHolderName`, `accountHolderEmail`). A field
 * ABSENT from the map has no reading yet — the same convention
 * `notApplicableFields` follows, and the reason both are JSON: the observed
 * field list is U44's to grow, and a new field must never cost a promotion.
 */
export const ObservedStandingFieldProvenanceSchema = z.record(z.string(), ObservedStandingProvenanceSchema)
export type ObservedStandingFieldProvenance = z.infer<typeof ObservedStandingFieldProvenanceSchema>

/**
 * The outcome of the LAST attempt to read a publishing account's standing from
 * the vendor portal.
 *
 * TWO VALUES, AND THE SECOND IS THE POINT. `stale_check` is a team the sweep
 * could not read — a degraded session, a 2FA failure, a walk that could not be
 * proven complete. It is recorded BESIDE the last real reading rather than
 * replacing it, because silence is not recovery: a check that did not run must
 * never clear a finding, and an empty read raises rather than blanking a field.
 *
 * A string column for the same reason as the provenance above — this vocabulary
 * is expected to grow (a selector miss below the page anchor is a plausible
 * third value) and growth must not cost a promotion.
 */
export const ObservedStandingCheckStatusSchema = z.enum(['observed', 'stale_check'])
export type ObservedStandingCheckStatus = z.infer<typeof ObservedStandingCheckStatusSchema>

/** Three states, never a boolean: an undecryptable key is not an empty slot (KTD4). */
export const AccountCredentialLivenessSchema = z.enum(['absent', 'present-and-retrievable', 'present-but-unverified'])
export type AccountCredentialLiveness = z.infer<typeof AccountCredentialLivenessSchema>

/**
 * One account record as every client sees it.
 *
 * `vendor` and `accountId` together are the natural key (R1) and are IMMUTABLE
 * after create: apps reach an account by that identifier copied onto the app row
 * with no foreign key, so editing it would silently orphan every app pointing at
 * it. Moving an app between accounts is a repoint of the app.
 */
export const PublishingAccountSchema = z.object({
  publicId: z.string(),
  vendor: PublishingAccountVendorSchema,
  /** The VENDOR's own identifier — an Apple team id, a Play developer account id. */
  accountId: z.string(),
  displayName: z.string().nullable(),
  ownership: PublishingAccountOwnershipSchema,
  kind: PublishingAccountKindSchema,
  /**
   * WHO can act in the vendor's portal (KD4). A name and an address, neither a
   * platform user — usually a school's IT staff, who is not one.
   *
   * The name is INDEPENDENT of the address and nullable on its own: an operator
   * who knows only a mailbox records the address without inventing a person.
   * The ADDRESS remains what the readiness verdict reads — a contact with a
   * name and no address is still nobody we can reach.
   */
  ownerContactName: z.string().nullable(),
  ownerContactEmail: z.string().nullable(),
  /** Can a vaulted credential read this account (R1). The same three values for every vendor. */
  apiAccessState: PublishingAccountApiAccessStateSchema,
  /** Non-null exactly when the state is `blocked` (R2). */
  apiAccessReason: PublishingAccountApiAccessReasonSchema.nullable(),
  /** ISO-8601, or null when nothing has been observed. Stored WITH the state so an observation is dateable. */
  apiAccessObservedAt: z.string().nullable(),
  /**
   * LEGACY, projected from the three fields above by {@link legacyAgreementStateFrom}.
   * Deployed clients still read these two; nothing derives anything from them.
   */
  agreementState: PublishingAccountAgreementStateSchema,
  agreementCheckedAt: z.string().nullable(),
  provisioningState: PublishingAccountProvisioningStateSchema,
  isRefused: z.boolean(),
  refusedAt: z.string().nullable(),
  refusedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
})
export type PublishingAccount = z.infer<typeof PublishingAccountSchema>

/**
 * A school an account publishes for.
 *
 * NOT a field on the account. An account has no `schoolId` and no foreign key —
 * one account can serve many schools (the shared School Journey team carries
 * most of the fleet), so the school lives on the many side, where it already is.
 */
export const PublishingAccountSchoolSchema = z.object({
  publicId: z.string(),
  name: z.string()
})
export type PublishingAccountSchool = z.infer<typeof PublishingAccountSchoolSchema>

/**
 * App Store Connect webhook coverage over the account's apps (SCH-1294 R9).
 *
 * Counts in the four per-app states of `AscWebhookReadinessSchema`. INDEPENDENT
 * of the verdict and of `provisioningState`: a delivery proves Apple can reach
 * us for one app, not that we hold a usable key, so these never contribute a
 * readiness reason and never move the provisioning axis.
 */
export const AscWebhookCoverageCountsSchema = z.object({
  registeredPinged: z.number().int().nonnegative(),
  registeredNeverPinged: z.number().int().nonnegative(),
  notRegistered: z.number().int().nonnegative(),
  cannotCheck: z.number().int().nonnegative()
})
export type AscWebhookCoverageCounts = z.infer<typeof AscWebhookCoverageCountsSchema>

/** An account with its verdict and what publishes under it (R13). */
export const PublishingAccountReadinessSchema = z.object({
  account: PublishingAccountSchema,
  verdict: ReadinessVerdictSchema,
  /**
   * What a human must do to unblock the account, as data (R2). Null unless
   * `account.apiAccessState` is `blocked` — an account nothing is blocking has
   * no step, and an empty object would read as one nobody has written yet.
   */
  blockedStep: PublishingAccountApiAccessBlockedStepSchema.nullable(),
  credentialLiveness: AccountCredentialLivenessSchema,
  /**
   * LEGACY — Apple-only webhook coverage, kept for ONE release beside `signal`
   * and removed with the rest of the bridge (U11). A Play account reports all
   * zeros here because it has no webhook lane at all, which is precisely the
   * blind spot `signal` exists to close.
   */
  webhooks: AscWebhookCoverageCountsSchema,
  /**
   * Vendor-neutral signal coverage over this account's apps (R15) — the five
   * counters that mean the same thing for Apple's push proof and Play's poll
   * proof, so one number on this row can be compared across vendors.
   *
   * Counted over the SHARED app-set predicate, so the five buckets sum to
   * `appCount` exactly; two spellings of "this account's apps" is how a total
   * stops reconciling against its own parts.
   */
  signal: SignalCoverageCountsSchema,
  /** Resolved by reverse lookup, not from a stored list — there is no foreign key. */
  appCount: z.number().int().nonnegative(),
  /**
   * Every school with an app under this account, deduped and name-sorted.
   *
   * A LIST, and the only trustworthy answer to "who is this account for":
   * `account.displayName` is free text someone typed, and it is not the legal
   * entity Apple shows either. Empty is a real answer — an account with no apps,
   * or apps with no school.
   */
  schools: z.array(PublishingAccountSchoolSchema)
})
export type PublishingAccountReadiness = z.infer<typeof PublishingAccountReadinessSchema>

/**
 * THE FIELD ALLOWLIST — every Membership Details value the sweep may persist,
 * and nothing else (Track I, U44's table; evidence rule 3).
 *
 * This union is the PII rule's only machine-readable expression. Apple's
 * Membership Details page also carries the customer's STREET ADDRESS and PHONE
 * NUMBER; both are read for a verdict and never stored, so neither has a member
 * here, neither has a column on
 * `PublishingAccountObservedStanding`, and the sweep's report schema is strict
 * so a field outside this set is a 400 rather than a silent extra column in a
 * JSON blob. Adding a member is a data-handling decision, not a fill-in.
 *
 * `accountHolderName` / `accountHolderEmail` ARE third-party PII and ARE stored:
 * super-admin-only wherever they render, and deliberately absent from the sweep's
 * FINDINGS (which land in a scheduled-execution row nothing gates on super-admin).
 */
export const OBSERVED_STANDING_FIELDS = [
  'enrolledAs',
  'entityName',
  'renewalDate',
  'ourRole',
  'accountHolderName',
  'accountHolderEmail'
] as const

export const ObservedStandingFieldSchema = z.enum(OBSERVED_STANDING_FIELDS)
export type ObservedStandingField = z.infer<typeof ObservedStandingFieldSchema>

/**
 * The values one successful read produced.
 *
 * STRICT, and every member optional-and-nullable. Strict is the enforcement: a
 * runner that starts reading the street address cannot deliver it, because the
 * request fails validation instead of quietly widening what we hold. Optional is
 * how "not read" stays distinguishable from "read as empty" — an absent key has
 * no reading, which is the same convention `fieldProvenance` and
 * `notApplicableFields` follow.
 */
export const ObservedStandingValuesSchema = z
  .object({
    enrolledAs: z.string().min(1).max(64).optional(),
    entityName: z.string().min(1).max(255).optional(),
    /** ISO-8601 date (`YYYY-MM-DD`). The column is a DATE — no time, no zone. */
    renewalDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'renewalDate must be an ISO-8601 date (YYYY-MM-DD)')
      .optional(),
    ourRole: z.string().min(1).max(64).optional(),
    accountHolderName: z.string().min(1).max(255).optional(),
    accountHolderEmail: z.string().min(1).max(255).optional()
  })
  .strict()
export type ObservedStandingValues = z.infer<typeof ObservedStandingValuesSchema>

/**
 * One account's current observed standing, as a client sees it.
 *
 * `checkStatus` and `lastCheckedAt` describe the LAST ATTEMPT; every value field
 * plus `observedAt` describe the last SUCCESSFUL read, and a failed attempt
 * never clears them. Reading this record therefore always answers two separate
 * questions — what the portal last said, and whether anyone has been able to ask
 * it lately.
 */
export const PublishingAccountObservedStandingSchema = z.object({
  /** `PublishingAccount.accountId` — the Apple team id. */
  accountId: z.string(),
  checkStatus: ObservedStandingCheckStatusSchema,
  checkError: z.string().nullable(),
  lastCheckedAt: z.string(),
  /** Null when the portal has never been read successfully for this account. */
  observedAt: z.string().nullable(),
  values: ObservedStandingValuesSchema,
  fieldProvenance: ObservedStandingFieldProvenanceSchema,
  /** Fields with no meaning for this enrollment kind or role — NOT missing. */
  notApplicableFields: z.array(ObservedStandingFieldSchema)
})
export type PublishingAccountObservedStanding = z.infer<typeof PublishingAccountObservedStandingSchema>

/**
 * Why the sweep is telling you about an account.
 *
 * A REASON vocabulary, not a status one — the same shape as
 * {@link ReadinessReasonSchema}, and deliberately NOT a fifth status vocabulary
 * (U46 forbids one). The status words stay
 * {@link ObservedStandingCheckStatusSchema}'s `observed` / `stale_check`, which
 * is U29's `STALE_CHECK` under the name U42 shipped for this table. Mapping
 * these reasons into the readiness vocabulary is U46's seam and is deliberately
 * not done here.
 *
 * NOTE ON `membership_removed` vs `stale_check`. Both leave the row at
 * `stale_check`, because in both cases nothing was read. What separates them is
 * whether the enumeration was PROVEN COMPLETE: absence from a walk that could
 * not be proven complete is `stale_check` and nothing more, because an
 * intermittent walk, pagination, or a degraded session produce the same signal.
 */
export const TeamStandingFindingKindSchema = z.enum([
  /** The renewal date the portal states is in the past. */
  'membership_lapsed',
  /** The renewal date is inside the warning window (30 days). */
  'membership_renewal_due',
  /** Proven absent from a COMPLETE team-selector walk. Otherwise invisible. */
  'membership_removed',
  /** Membership Details did not render at all — the page-level anchor missed. */
  'selector_miss',
  /** Read attempted, nothing usable came back. Silence is never recovery. */
  'stale_check',
  /** The portal disagrees with an operator-asserted `kind` or `ownership` (D6). */
  'record_disagreement'
])
export type TeamStandingFindingKind = z.infer<typeof TeamStandingFindingKindSchema>

/**
 * One finding.
 *
 * CARRIES NO PII. Findings ride back in the sweep's scheduled-execution `result`
 * JSON, which no super-admin gate covers — so the account holder's name and
 * email stay in the observed-standing row and out of here, and `detail` is
 * written by the platform rather than copied off the page. For the same reason
 * a lapsed membership names the **business office** as the contact and never the
 * Account Holder.
 */
export const TeamStandingFindingSchema = z.object({
  kind: TeamStandingFindingKindSchema,
  /** The Apple team id. Never a school name, never a person. */
  accountId: z.string(),
  detail: z.string(),
  /** Who to talk to, where the finding implies someone. */
  contact: z.string().optional()
})
export type TeamStandingFinding = z.infer<typeof TeamStandingFindingSchema>
